import React, { FC } from 'react';
import { toast } from 'react-toastify';
import { Button, Card, CardBody, CardHeader, Col } from 'reactstrap';
import { ExtendedUser, UserRole } from '../../../../shared/user/extended';
import { RoleWithPermissions } from '../../../../shared/user/roles';
import { RoleScope } from '../../../APIClient';
import { useTranslation } from '../../../translations';
import { capitalize, Fetcher, useConvention, useFetcher } from '../../../utils';
import { captureError } from '../../../utils/errorHandling';

interface RolesTabProps {
  readonly userData: ExtendedUser;
  readonly refreshContents: (force?: boolean) => void;
}

export const RolesTab: FC<RolesTabProps> = ({ userData, refreshContents }) => {
  const fetcher = useFetcher(async () => await api.getUserRoles());

  if (!fetcher.success) {
    return <Fetcher result={fetcher} />;
  }

  return fetcher.data.map((r) => {
    return (
      <RoleRow
        key={r.id}
        refresh={() => {
          refreshContents(true);
        }}
        role={r}
        roles={userData.roles!}
        userId={userData.id}
      />
    );
  });
};

interface RoleRowProps {
  readonly role: RoleWithPermissions;
  readonly roles: UserRole[];
  readonly userId: number;
  readonly refresh: () => void;
}

const RoleRow: FC<RoleRowProps> = ({ role, roles, refresh, userId }) => {
  const { id } = role;
  const convention = useConvention();
  const hasConvention = roles.some((r) => r.id === id && r.conventionIds.includes(convention.id));
  const hasGlobal = roles.some((r) => r.id === id && r.conventionIds.includes(0));
  const { ts } = useTranslation();

  const toggleRole = async (type: RoleScope) => {
    const remove = type === 'convention' ? hasConvention : hasGlobal;
    try {
      await (remove
        ? api.updateUserRole(userId, id, 'remove', type)
        : api.updateUserRole(userId, id, 'add', type));

      toast.success(ts(remove ? 'role_removed' : 'role_added', role.name));
      refresh();
    } catch (error) {
      captureError(error as Error);
    }
  };

  return (
    <Col className="mt-10" lg={4} xs={12}>
      <Card>
        <CardHeader>
          {role.name}
          <RoleRowButton enabled={hasConvention} id={id} title="convention" toggle={toggleRole} />
          <RoleRowButton enabled={hasGlobal} id={id} title="global" toggle={toggleRole} />
        </CardHeader>
        {role.description && <CardBody>{role.description}</CardBody>}
      </Card>
    </Col>
  );
};

interface RoleRowButtonProps {
  readonly id: number;
  readonly enabled: boolean;
  readonly title: 'convention' | 'global';
  readonly toggle: (scope: RoleScope) => void;
}

export const RoleRowButton: FC<RoleRowButtonProps> = ({ id, enabled, title, toggle }) => {
  return (
    <Button
      className="float-end"
      color={enabled ? 'primary' : 'secondary'}
      id={`roleRowButton-${id}-${title}`}
      onClick={() => {
        toggle(title);
      }}
      style={{ padding: '0px 5px', marginRight: '5px' }}
    >
      {capitalize(title)}
    </Button>
  );
};
